import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, unref as _unref, createBlock as _createBlock, createCommentVNode as _createCommentVNode, createElementVNode as _createElementVNode, createVNode as _createVNode, Transition as _Transition, withCtx as _withCtx } from "vue"

const _hoisted_1 = { class: "filter-box" }
const _hoisted_2 = { class: "button-row" }

import { DashboardDropdownFilter } from '../components/filters'
import { BuildingFeature, ComplexFeature, RealEstateFeature } from '../../types'

import ButtonOutline from '@/design-system/atoms/buttons/ButtonOutline.vue'
import ButtonPrimary from '@/design-system/atoms/buttons/ButtonPrimary.vue'
import { ref, watch, computed, nextTick } from 'vue'
import { useStore } from 'vuex'
import { useI18n } from 'vue-i18n'
import { InformationResponse } from '@/api-client/models/utilization/Contracts'
import { CoreEntityDto, CoreType } from '@/interfaces/core.interface'
import { getTitleKey } from '@/hooks/reports/useReport'
import { getRealmId } from '@/utils/realmId'
import { useSelectionStore } from '@/pinia/selectionNew'
import { getLevelsForBuilding } from '@/services/floors.service'
import { LEVEL } from '@/interfaces/level.interface'
import useNavigateTo from '@/composables/useNavigateTo'

type Props = {
  information: InformationResponse | undefined
  isCollapsed: boolean
}
type Emits = {
  (e: 'resetForm'): void
  (e: 'submitForm'): void
}
type Entity = {
  id: string
  name: string
  type: CoreType
  parentId?: string
}

type Options = {
  multiSelect: boolean
  disabled: boolean
  hidden: boolean
  requireParent: boolean
}


export default /*@__PURE__*/_defineComponent({
  __name: 'AggregateAnalysisFilters',
  props: {
    information: {},
    isCollapsed: { type: Boolean }
  },
  emits: ["resetForm", "submitForm"],
  setup(__props: any, { emit: __emit }) {

const props = __props
const emits = __emit
const vuex = useStore()
const { navigateTo } = useNavigateTo()
const { t } = useI18n()

const defaultOptions = { multiSelect: false, disabled: false, hidden: false, requireParent: false }

const initialOptions: Partial<Record<CoreType, Partial<Options>>> = {
  CONSTRUCTION: { multiSelect: true },
  LEVEL: { requireParent: true },
  SPACE: { hidden: true, requireParent: true }
}

const store = useStore()
const selection = useSelectionStore()

const htmlRef = ref<HTMLElement | null>(null)

const entityLevels = ref<CoreType[]>(['COMPLEX', 'REAL_ESTATE', 'CONSTRUCTION', 'LEVEL', 'SPACE'])

const entityOptions = ref<Record<CoreType, Options>>(
  entityLevels.value.reduce((acc, x) => {
    acc[x] = { ...defaultOptions, ...initialOptions[x] }
    return acc
  }, {} as Record<CoreType, Options>)
)

const entityLevelOptions = computed<[CoreType, Options][]>(() =>
  entityLevels.value.map((x) => [x, { ...defaultOptions, ...entityOptions.value[x] }])
)

const getLabel = (type: CoreType) =>
  t(getTitleKey(type, entityOptions.value[type].multiSelect, getRealmId() ?? ''))

const isCurrentFilterSubmitted = ref<boolean>(false)

const emptySelections = () =>
  entityLevelOptions.value.reduce((acc, [type]) => {
    acc[type] = []
    return acc
  }, {} as Record<CoreType, Entity[]>)

const selections = ref<Record<CoreType, Entity[]>>(emptySelections())

const entityMap = computed<Record<CoreType, Entity[]>>(() =>
  entityLevelOptions.value.reduce((acc, [type]) => {
    acc[type] = filteredEntities.value.filter((y) => y.type === type)
    return acc
  }, {} as Record<CoreType, Entity[]>)
)

const nameMaps = computed<Record<CoreType, Record<string, string>>>(() =>
  Object.entries(entityMap.value).reduce((acc, [key, value]) => {
    acc[key] = value.reduce((acc2, x) => {
      acc2[x.id] = x.name
      return acc2
    }, {} as Record<string, string>)
    return acc
  }, {} as Record<CoreType, Record<string, string>>)
)

const levelsForSelectedBuildings = ref<LEVEL[]>([])

const loadedEntities = computed<Entity[]>(() => {
  // Create the level list from the async API results…
  const fetchedLevels = levelsForSelectedBuildings.value.map(
    (x): Entity => ({
      id: x.properties.id,
      name: x.properties.displayName,
      parentId: x.properties.constructionId,
      type: 'LEVEL'
    })
  )
  // …but also add any level that’s already selected. This ensures that if a user selects a level
  // before the asynchronous call returns, it will still be in the list.
  selections.value.LEVEL.forEach((selectedLevel) => {
    if (!fetchedLevels.find((lvl) => lvl.id === selectedLevel.id)) {
      fetchedLevels.push(selectedLevel)
    }
  })
  return [
    ...(vuex.getters.allComplexes?.map(
      (x: ComplexFeature): Entity => ({
        id: x.properties.belongsToId,
        name: x.properties.displayName,
        type: 'COMPLEX'
      })
    ) ?? []),
    ...(vuex.getters.allRealEstates.map(
      (x: RealEstateFeature): Entity => ({
        id: x.properties.id,
        name: x.properties.displayName,
        parentId: x.properties.complexId,
        type: 'REAL_ESTATE'
      })
    ) ?? []),
    ...(vuex.getters.allBuildings.map(
      (x: BuildingFeature): Entity => ({
        id: x.properties.id,
        name: x.properties.displayName,
        parentId: x.properties.realEstateId,
        type: 'CONSTRUCTION'
      })
    ) ?? []),
    ...fetchedLevels,
    ...(vuex.state.roomsForSelectedLevel?.data?.features?.map(
      (x): Entity => ({
        id: x.properties.id,
        name: x.properties.displayName,
        parentId: x.properties.levelId,
        type: 'SPACE'
      })
    ) ?? [])
  ]
})

const filteredEntities = computed<Entity[]>(() => {
  return loadedEntities.value.filter((entity) => {
    // COMPLEX – top-level, always show.
    if (entity.type === 'COMPLEX') {
      return true
    }

    // REAL_ESTATE – if a Complex is selected, only show real estates with that parent.
    // Otherwise, show all real estates.
    if (entity.type === 'REAL_ESTATE') {
      if (selections.value.COMPLEX.length > 0) {
        return selections.value.COMPLEX.some((complex) => complex.id === entity.parentId)
      }
      return true
    }

    // CONSTRUCTION
    // If no real estate is selected but a complex is, then use the complex membership.
    // Otherwise, show all buildings.
    if (entity.type === 'CONSTRUCTION') {
      if (selections.value.REAL_ESTATE.length > 0) {
        return selections.value.REAL_ESTATE.some((re) => re.id === entity.parentId)
      }
      if (selections.value.COMPLEX.length > 0) {
        // Look up the real estate for this building so we know its complex.
        const associatedRE = loadedEntities.value.find(
          (e) => e.type === 'REAL_ESTATE' && e.id === entity.parentId
        )
        if (associatedRE) {
          return selections.value.COMPLEX.some((complex) => complex.id === associatedRE.parentId)
        }
        return false
      }
      return true
    }

    // LEVEL – since levels depend directly on buildings we typically require
    // a building to be selected.
    if (entity.type === 'LEVEL') {
      return (
        selections.value.CONSTRUCTION.length === 1 &&
        selections.value.CONSTRUCTION[0].id === entity.parentId
      )
    }

    // SPACE – similar logic: only show spaces when a level is explicitly selected.
    if (entity.type === 'SPACE') {
      return selections.value.LEVEL.length === 1 && selections.value.LEVEL[0].id === entity.parentId
    }

    // Default fallback.
    return true
  })
})

watch(
  () => selection.entityPath,
  (entityPath: CoreEntityDto[][]) => {
    clearSelections()
    const lastEntities = entityPath.at(entityPath.length - 1)
    if (!lastEntities || lastEntities.length === 0) {
      return
    }
    const type = lastEntities.at(0)?.type
    if (!type) {
      return
    }
    selectionChanged(
      type,
      lastEntities.map((x) => x.id)
    )
  },
  { immediate: true }
)

watch(
  () => props.isCollapsed,
  async () => {
    await nextTick()
    htmlRef.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  },
  { immediate: true }
)

const levelsCache = ref<Record<string, LEVEL[]>>({})
watch(
  () => selections.value.CONSTRUCTION,
  async (constructions) => {
    // First, ensure the constructions array itself is unique.
    const uniqueConstructions = [...new Map(constructions.map((b) => [b.id, b])).values()]

    const promises = uniqueConstructions.map(async (building) => {
      if (!levelsCache.value[building.id]) {
        const result = await getLevelsForBuilding(building.id, undefined, false)
        levelsCache.value[building.id] = result?.data?.features ?? []
      }
      return levelsCache.value[building.id]
    })
    const arraysOfLevels = await Promise.all(promises)
    // Flatten the array of arrays.
    const flattenedLevels = arraysOfLevels.flat()
    // Deduplicate levels using the level id.
    const dedupedLevels = [
      ...new Map(flattenedLevels.map((lvl) => [lvl.properties.id, lvl])).values()
    ]
    levelsForSelectedBuildings.value = dedupedLevels
  },
  { immediate: true }
)

async function submit() {
  const lowestLevelSelection = entityLevels.value
    .toReversed()
    .map((x) => selections.value[x])
    .find((x) => x.length > 0)
  if (lowestLevelSelection) {
    const ids = lowestLevelSelection.map((x) => x.id)
    let zoomIds = ids
    if (lowestLevelSelection.some((x) => x.type === 'LEVEL')) {
      zoomIds = lowestLevelSelection.map((x) => x.parentId ?? x.id)
    }
    selection.set(ids, true, zoomIds)
  } else {
    selection.clear()
  }
  if (lowestLevelSelection?.length === 1) {
    navigateTo(lowestLevelSelection[0].id)
  }
  if (lowestLevelSelection === undefined) {
    store.dispatch('RESET_ALL_SELECTIONS')
    store.commit('SET_CLICKED_ITEM_TYPE', { type: 'region' })
    selection.clear()
  }
  emits('submitForm')
}

function resetForm() {
  clearSelections()
  emits('resetForm')
}

function parentType(type: CoreType) {
  switch (type) {
    case 'REAL_ESTATE':
      return 'COMPLEX'
    case 'CONSTRUCTION':
      return 'REAL_ESTATE'
    case 'LEVEL':
      return 'CONSTRUCTION'
    case 'SPACE':
      return 'LEVEL'
    default:
      return ''
  }
}

function isMultiSelectAllowed(type: CoreType) {
  return !!selections.value[parentType(type)]?.length
}

function clearSelectionsFrom(changedType: CoreType) {
  const startIndex = entityLevels.value.indexOf(changedType) + 1
  for (let i = startIndex; i < entityLevels.value.length; i++) {
    const lowerType = entityLevels.value[i]
    selections.value[lowerType] = [] // clear only lower-level selections
  }
}
function clearSelections() {
  selections.value = emptySelections()
}

function uniqueEntities(entities: Entity[]): Entity[] {
  const map = new Map<string, Entity>()
  for (const e of entities) {
    if (!map.has(e.id)) {
      map.set(e.id, e)
    }
  }
  return Array.from(map.values())
}

function setSelection(type: CoreType, ids: string[]) {
  selections.value[type] = uniqueEntities(entityMap.value[type].filter((x) => ids.includes(x.id)))
}

function addSelection(type: CoreType, ids: string[]) {
  const currentSelection = selections.value[type]
  const newSelections = entityMap.value[type].filter((x) => ids.includes(x.id))
  // Merge and deduplicate by id.
  selections.value[type] = uniqueEntities([...currentSelection, ...newSelections])
}

function getParents(ids: string[]): Entity[] {
  const entities = ids
    .map((id) => loadedEntities.value.find((x) => x.id === id))
    .filter((x) => x !== undefined) as Entity[]
  return entities
    .map((entity) => loadedEntities.value.find((x) => x.id === entity.parentId))
    .filter((x) => x !== undefined) as Entity[]
}

function recurseAddParents(type: CoreType, ids: string[]) {
  const parents = getParents(ids)
  if (parents.length === 0) {
    return
  }
  addSelection(
    parents[0].type,
    parents.map((x) => x.id)
  )
  parents.forEach((parent) => recurseAddParents(parent.type, [parent.id]))
}

function selectionChanged(type: CoreType, ids: string[]) {
  clearSelectionsFrom(type)
  recurseAddParents(type, ids)
  setSelection(type, ids)
}

return (_ctx: any,_cache: any) => {
  return (!_ctx.isCollapsed)
    ? (_openBlock(), _createBlock(_Transition, {
        key: 0,
        name: "slide"
      }, {
        default: _withCtx(() => [
          _createElementVNode("div", _hoisted_1, [
            _createElementVNode("div", {
              ref_key: "htmlRef",
              ref: htmlRef,
              class: "filter-container"
            }, [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(entityLevelOptions.value, ([type, options]) => {
                return (_openBlock(), _createElementBlock(_Fragment, { key: type }, [
                  (!options.hidden)
                    ? (_openBlock(), _createBlock(_unref(DashboardDropdownFilter), {
                        key: 0,
                        options: entityMap.value[type].map((x) => x.id),
                        selections: selections.value[type].map((x) => x.id),
                        "present-as-multi-select": options.multiSelect && isMultiSelectAllowed(type),
                        label: getLabel(type),
                        searchType: getLabel(type).toLowerCase(),
                        optionsWithData: undefined,
                        "name-map": nameMaps.value[type],
                        onSelectionChanged: (ids) => selectionChanged(type, ids)
                      }, null, 8, ["options", "selections", "present-as-multi-select", "label", "searchType", "name-map", "onSelectionChanged"]))
                    : _createCommentVNode("", true)
                ], 64))
              }), 128))
            ], 512),
            _createElementVNode("div", _hoisted_2, [
              _createVNode(ButtonOutline, {
                size: "small",
                buttonText: _unref(t)('general__text__dashboard_reset_selections'),
                onOnClicked: resetForm
              }, null, 8, ["buttonText"]),
              _createVNode(ButtonPrimary, {
                size: "small",
                disabled: isCurrentFilterSubmitted.value,
                buttonText: _unref(t)('general__text__dashboard_update_selections'),
                onOnClicked: submit
              }, null, 8, ["disabled", "buttonText"])
            ])
          ])
        ]),
        _: 1
      }))
    : _createCommentVNode("", true)
}
}

})